import {
  AuditOutlined,
  CarOutlined,
  CommentOutlined,
  CrownOutlined,
  DashboardOutlined,
  FormOutlined,
  NotificationOutlined,
  RobotOutlined,
  ShopOutlined,
  SmileOutlined,
  TableOutlined,
  ToolOutlined,
  UserOutlined,
} from '@ant-design/icons';
import type { Settings as LayoutSettings } from '@ant-design/pro-components';
import { SettingDrawer } from '@ant-design/pro-components';
import type { MenuDataItem } from '@ant-design/pro-layout';
import type { RunTimeLayoutConfig } from '@umijs/max';
import { history } from '@umijs/max';
import { message, Select, Space, Spin, theme } from 'antd';
import React, { useEffect } from 'react';
import { AvatarDropdown, AvatarName, HeaderDistrictSelect } from '@/components';
import { getUserInfo, type UserInfoData } from '@/pages/login/api';
import { post } from '@/services/request';
import { clearSelectedDistrictCode, resolveInitialDistrictCode, setSelectedDistrictCode } from '@/utils/tools';
import {
  buildDistrictSelectOptions,
  getDistrictNamesByCodes,
  type DistrictEnumProvinceItem,
} from '@/utils/emnu';
import defaultSettings from '../config/defaultSettings';
import '@ant-design/v5-patch-for-react-19';

const isDev = process.env.NODE_ENV === 'development';
const loginPath = '/user/login';
const SYSTEM_TYPE_STORAGE_KEY = 'systemType';
const SHARED_BIKE_ROUTE_PREFIX = '/sharedBike';

type SystemType = 'smoke' | 'sharedBike';

type UserMenuItem = {
  path: string;
  name: string;
  icon?: string;
  redirect?: string;
  children?: UserMenuItem[];
};

type PassportConfData = Record<string, unknown>;

type PassportConfResponse = {
  code: number;
  msg: string;
  traceId: string;
  data?: PassportConfData;
};

type InitialState = {
  fetchUserInfo: () => Promise<UserInfoData | undefined>;
  currentUser?: UserInfoData;
  passportConf?: PassportConfData;
  menuData?: UserMenuItem[];
  selectedDistrictCode?: number;
  systemType: SystemType;
  settings: Partial<LayoutSettings>;
};

/** 菜单 icon 字符串转 ProLayout 所需的 ReactNode，与路由配置的 icon 命名一致 */
const MENU_ICON_MAP: Record<string, React.ReactNode> = {
  smile: <SmileOutlined />,
  table: <TableOutlined />,
  crown: <CrownOutlined />,
  shop: <ShopOutlined />,
  // 净化设备管理 - 设备/工具
  'air-purifier': <ToolOutlined />,
  // 清洗记录管理 - 表单/记录
  'cleaning-record': <FormOutlined />,
  // 油烟监测管理 - 数据/监测
  'smoke-monitor': <DashboardOutlined />,
  // 巡查检测管理 - 审核/巡查
  inspection: <AuditOutlined />,
  // 群众投诉管理 - 反馈/投诉
  complaint: <CommentOutlined />,
  // 通知公告管理
  notification: <NotificationOutlined />,
  // 账号管理
  user: <UserOutlined />,
  // 数据看板
  'data-board': <DashboardOutlined />,
  // 自动化工具
  automation: <RobotOutlined />,
  // 共享单车管理
  bicycle: <CarOutlined />,
};

const getStoredSystemType = (): SystemType => {
  return localStorage.getItem(SYSTEM_TYPE_STORAGE_KEY) === 'sharedBike'
    ? 'sharedBike'
    : 'smoke';
};

const getUserSubRoleTypes = (currentUser?: UserInfoData): number[] => {
  if (Array.isArray(currentUser?.subRoleTypes)) {
    return currentUser.subRoleTypes.map(Number);
  }
  try {
    const stored = JSON.parse(localStorage.getItem('subRoleTypes') || '[]');
    return Array.isArray(stored) ? stored.map(Number) : [];
  } catch {
    return [];
  }
};

/** 优先取 currentUser.roleType，避免 undefined 时 Number 得到 NaN */
const getUserRoleType = (currentUser?: UserInfoData): number => {
  return Number(currentUser?.roleType ?? localStorage.getItem('roleType'));
};

/** roleType=3 时：31=油烟，34=单车；其他角色默认两套系统都可切换 */
const getAvailableSystemTypes = (currentUser?: UserInfoData): SystemType[] => {
  const roleType = getUserRoleType(currentUser);
  if (roleType !== 3) {
    return ['smoke', 'sharedBike'];
  }

  const subRoleTypes = getUserSubRoleTypes(currentUser);
  const systems: SystemType[] = [];
  if (subRoleTypes.includes(31)) systems.push('smoke');
  if (subRoleTypes.includes(34)) systems.push('sharedBike');
  return systems.length > 0 ? systems : ['smoke'];
};

const resolveSystemType = (
  preferred: SystemType,
  currentUser?: UserInfoData,
): SystemType => {
  const available = getAvailableSystemTypes(currentUser);
  if (available.includes(preferred)) return preferred;
  return available[0] ?? 'smoke';
};

/** 将后端返回的 API.MenuItem[] 转为 ProLayout MenuDataItem[] */
function convertUserMenuToProMenu(items: UserMenuItem[]): MenuDataItem[] {
  return items.map((item) => ({
    path: item.path,
    name: item.name,
    icon: item.icon ? (MENU_ICON_MAP[item.icon] ?? item.icon) : undefined,
    redirect: item.redirect,
    children:
      item.children && item.children.length > 0
        ? convertUserMenuToProMenu(item.children)
        : undefined,
  }));
}

/** 将路由默认菜单的 icon 字符串转为 ReactNode，与 routes 里配置的 icon 一致 */
function patchMenuIcons(items: MenuDataItem[]): MenuDataItem[] {
  return items.map((item) => ({
    ...item,
    icon:
      typeof item.icon === 'string'
        ? (MENU_ICON_MAP[item.icon] ?? item.icon)
        : item.icon,
    children:
      item.children && item.children.length > 0
        ? patchMenuIcons(item.children)
        : item.children,
  }));
}

function filterMenuBySystem(items: MenuDataItem[], systemType: SystemType): MenuDataItem[] {
  return items.filter((item) => {
    const path = item.path || '';
    const isSharedBikeMenu = path.startsWith(SHARED_BIKE_ROUTE_PREFIX);
    return systemType === 'sharedBike' ? isSharedBikeMenu : !isSharedBikeMenu;
  });
}

/** 获取用户区域标题 */
function getUserRegionTitle(
  currentUser?: UserInfoData,
  selectedDistrictCode?: number,
) {
  const roleType = currentUser?.roleType;
  if (![1, 3, 6, 7].includes(Number(roleType))) return undefined;
  if (selectedDistrictCode == null) return undefined;

  const districtEnum = Array.isArray(currentUser?.districtEnum)
    ? (currentUser.districtEnum as DistrictEnumProvinceItem[])
    : [];
  const districtName = getDistrictNamesByCodes({
    districtEnum,
    districtCodes: selectedDistrictCode,
    emptyText: '',
  });

  return districtName || undefined;
}

/** 获取初始化设置 */
function getInitialSettings(
  currentUser?: UserInfoData,
  selectedDistrictCode?: number,
): Partial<LayoutSettings> {
  const regionTitle = getUserRegionTitle(currentUser, selectedDistrictCode);

  return {
    ...(defaultSettings as Partial<LayoutSettings>),
    ...(regionTitle ? { title: `${regionTitle}-油烟智慧化平台` } : {}),
  };
}

function getSystemSettings(
  systemType: SystemType,
  currentUser?: UserInfoData,
  selectedDistrictCode?: number,
): Partial<LayoutSettings> {
  if (systemType === 'sharedBike') {
    return {
      ...(defaultSettings as Partial<LayoutSettings>),
      title: '共享单车管理平台',
    };
  }

  return getInitialSettings(currentUser, selectedDistrictCode);
}

/** 无权限时（未登录）重定向到登录页，符合 Pro 的 access 设计 */
const RedirectToLogin: React.FC = () => {
  useEffect(() => {
    const { pathname, search } = history.location;
    const redirect =
      pathname !== loginPath ? `${pathname}${search}` : undefined;
    history.replace({
      pathname: loginPath,
      search: redirect
        ? `?redirect=${encodeURIComponent(redirect)}`
        : undefined,
    });
  }, []);
  return (
    <div style={{ paddingTop: 100, textAlign: 'center' }}>
      <Spin size="large" tip="请先登录…" />
    </div>
  );
};

/**
 * @see https://umijs.org/docs/api/runtime-config#getinitialstate
 * */
export async function getInitialState(): Promise<InitialState> {
  const fetchUserInfo = async () => {
    try {
      const res = await getUserInfo();
      if (res.code === 200 && res.data) {
        return res.data;
      }
    } catch (_error) {
      // ignore and treat as unauthenticated
    }
    localStorage.removeItem('token');
    localStorage.removeItem('roleType');
    clearSelectedDistrictCode();
    return undefined;
  };

  /** 获取护照配置 */
  const fetchPassportConf = async () => {
    try {
      const res = await post<PassportConfResponse>('/passport/conf');
      if (res.code === 200 && res.data) {
        return res.data;
      }
    } catch (_error) {
      // 枚举获取失败不影响登录流程，页面按空配置处理即可
    }
    return undefined;
  };

  const token = localStorage.getItem('token');
  const storedSystemType = getStoredSystemType();
  if (!token) {
    clearSelectedDistrictCode();
    history.push(loginPath);
    return {
      fetchUserInfo,
      currentUser: undefined,
      systemType: storedSystemType,
      settings: getSystemSettings(storedSystemType),
    };
  }
  const currentUser = token ? await fetchUserInfo() : undefined;

  if (currentUser?.roleType == 2) {
    message.error('您没有权限访问该系统！');
    clearSelectedDistrictCode();
    return {
      fetchUserInfo,
      currentUser: undefined,
      systemType: storedSystemType,
      settings: getSystemSettings(storedSystemType),
    };
  }

  const systemType = resolveSystemType(storedSystemType, currentUser);
  if (systemType !== storedSystemType) {
    localStorage.setItem(SYSTEM_TYPE_STORAGE_KEY, systemType);
  }

  const passportConf = currentUser ? await fetchPassportConf() : undefined;
  let districtCodes: number | string = '';
  if (currentUser?.roleType == 1) {
    districtCodes = 411122;
  } else {
    const districtEnum = Array.isArray(currentUser?.districtEnum)
      ? (currentUser.districtEnum as DistrictEnumProvinceItem[])
      : [];
    districtCodes = buildDistrictSelectOptions(districtEnum)[0]?.value ?? '';
  }
  const selectedDistrictCode = resolveInitialDistrictCode(districtCodes);
  setSelectedDistrictCode(selectedDistrictCode);
  return {
    fetchUserInfo,
    currentUser,
    passportConf,
    selectedDistrictCode,
    systemType,
    settings: getSystemSettings(systemType, currentUser, selectedDistrictCode),
  };
}

// ProLayout 支持的api https://procomponents.ant.design/components/layout
export const layout: RunTimeLayoutConfig = ({
  initialState,
  setInitialState,
}) => {
  const selectedSystem = initialState?.systemType ?? 'smoke';
  const { token } = theme.useToken();

  const handleSystemChange = async (systemType: SystemType) => {
    if (!initialState || systemType === selectedSystem) return;

    localStorage.setItem(SYSTEM_TYPE_STORAGE_KEY, systemType);
    await setInitialState({
      ...initialState,
      systemType,
      settings: getSystemSettings(
        systemType,
        initialState.currentUser,
        initialState.selectedDistrictCode,
      ),
    });
    history.push(systemType === 'sharedBike' ? SHARED_BIKE_ROUTE_PREFIX : '/welcome');
  };

  const renderSystemOption = (
    title: string,
    description: string,
    icon: React.ReactNode,
  ) => (
    <Space align="start" size={8}>
      <div
        style={{
          width: 30,
          height: 30,
          borderRadius: 10,
          display: 'grid',
          placeItems: 'center',
          background: token.colorPrimaryBg,
          color: token.colorPrimary,
        }}
      >
        {icon}
      </div>
      <div>
        <div style={{ fontWeight: 600, color: token.colorText }}>{title}</div>
        <div style={{ fontSize: 12, color: token.colorTextSecondary }}>{description}</div>
      </div>
    </Space>
  );

  return {
    actionsRender: () => {
      if (!initialState?.currentUser) return [];

      const availableSystems = getAvailableSystemTypes(initialState.currentUser);
      const actions: React.ReactNode[] = [];
      if (selectedSystem === 'smoke') {
        actions.push(<HeaderDistrictSelect key="header-district-select" />);
      }
      if (availableSystems.length > 0) {
        actions.push(
          <Select<SystemType>
            key="system-switch"
            value={selectedSystem}
            open={availableSystems.length > 1 ? undefined : false}
            style={{
              minWidth: 230,
              borderRadius: 999,
              background: token.colorFillAlter,
              boxShadow: `0 1px 6px ${token.colorBorderSecondary}`,
            }}
            size="middle"
            bordered={false}
            dropdownMatchSelectWidth={320}
            optionLabelProp="shortLabel"
            onChange={(value) => handleSystemChange(value)}
            suffixIcon={
              selectedSystem === 'sharedBike' ? (
                <CarOutlined style={{ color: token.colorPrimary }} />
              ) : (
                <DashboardOutlined style={{ color: token.colorPrimary }} />
              )
            }
            options={[
              {
                value: 'smoke' as const,
                shortLabel: '油烟智慧化平台',
                label: renderSystemOption(
                  '油烟智慧化平台',
                  '餐饮油烟治理 / 设备监测',
                  <DashboardOutlined />,
                ),
              },
              {
                value: 'sharedBike' as const,
                shortLabel: '共享单车管理平台',
                label: renderSystemOption(
                  '共享单车管理平台',
                  '车辆调度 / 运维监控',
                  <CarOutlined />,
                ),
              },
            ].filter((option) => availableSystems.includes(option.value))}
          />,
        );
      }
      return actions;
    },
    avatarProps: {
      src: 'https://static.yyzhhpt.com/upload/image/20260922/26092210114U92.png',
      title: <AvatarName />,
      render: (_, avatarChildren) => {
        return <AvatarDropdown>{avatarChildren}</AvatarDropdown>;
      },
    },
    title: getSystemSettings(
      selectedSystem,
      initialState?.currentUser,
      initialState?.selectedDistrictCode,
    )?.title,

    onPageChange: () => {
      const { location } = history;
      // 如果没有登录，重定向到 login
      if (!initialState?.currentUser && location.pathname !== loginPath) {
        history.push(loginPath);
        return;
      }

      const roleType = getUserRoleType(initialState?.currentUser);
      if (roleType === 3 && location.pathname !== loginPath) {
        const subRoleTypes = getUserSubRoleTypes(initialState?.currentUser);
        const hasSmoke = subRoleTypes.includes(31);
        const hasSharedBike = subRoleTypes.includes(34);
        const isSharedBikeRoute = location.pathname.startsWith(SHARED_BIKE_ROUTE_PREFIX);

        // 仅单车权限：非 sharedBike 路由重定向到单车首页
        if (hasSharedBike && !hasSmoke && !isSharedBikeRoute) {
          history.push(SHARED_BIKE_ROUTE_PREFIX);
          return;
        }
        // 仅油烟权限：sharedBike 路由重定向到油烟首页
        if (hasSmoke && !hasSharedBike && isSharedBikeRoute) {
          history.push('/welcome');
        }
      }
    },
    bgLayoutImgList: [
      {
        src: 'https://mdn.alipayobjects.com/yuyan_qk0oxh/afts/img/D2LWSqNny4sAAAAAAAAAAAAAFl94AQBr',
        left: 85,
        bottom: 100,
        height: '303px',
      },
      {
        src: 'https://mdn.alipayobjects.com/yuyan_qk0oxh/afts/img/C2TWRpJpiC0AAAAAAAAAAAAAFl94AQBr',
        bottom: -68,
        right: -45,
        height: '303px',
      },
      {
        src: 'https://mdn.alipayobjects.com/yuyan_qk0oxh/afts/img/F6vSTbj8KpYAAAAAAAAAAAAAFl94AQBr',
        bottom: 0,
        left: 0,
        width: '331px',
      },
    ],
    menuHeaderRender: undefined,
    // 根据当前登录用户接口返回的菜单展示左侧菜单，无则使用路由默认菜单
    menuDataRender: (defaultMenuData) => {
      const userMenu = initialState?.menuData;
      if (selectedSystem === 'sharedBike') {
        return filterMenuBySystem(patchMenuIcons(defaultMenuData), selectedSystem);
      }
      if (userMenu?.length) {
        return filterMenuBySystem(convertUserMenuToProMenu(userMenu), selectedSystem);
      }
      // 对路由默认菜单的 icon 做映射，否则 routes 里自定义 icon 名（如 air-purifier）不会显示
      return filterMenuBySystem(patchMenuIcons(defaultMenuData), selectedSystem);
    },
    // 未登录访问需权限页面时，重定向到登录页（与 access.isLoggedIn 配合）
    unAccessible: <RedirectToLogin />,
    childrenRender: (children) => {

      return (
        <>
          {children}
          {isDev && (
            <SettingDrawer
              disableUrlParams
              enableDarkTheme
              settings={initialState?.settings}
              onSettingChange={(settings) => {
                if (!initialState) return;
                setInitialState({
                  ...initialState,
                  settings,
                });
              }}
            />
          )}
        </>
      );
    },
    ...initialState?.settings,
  };
};
